import { Box, IconButton, Typography, Tooltip } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import GroupIcon from "@mui/icons-material/Group";
import { useNavigate } from "react-router-dom";
import { ClientInformation } from "../../client-config/data/types";
import ROUTES from "../../../routes/constants";
import CustomizedDataGrid from "../../customized-data-grid/customized-data-grid";
import { ClientWithFrequency } from "../types";
import AutoComplete from "../../client-information/auto-complete";

interface ClientListTableProps {
  clients: ClientWithFrequency[];
  allClients: ClientWithFrequency[];
  onViewClient: (client: ClientInformation) => void;
  getFileCount: (clientId: number) => number;
  searchValue: ClientInformation | null;
  onClientSearchChange: (value: ClientInformation | null) => void;
}

const ClientListTable = ({
  clients,
  allClients,
  onViewClient,
  getFileCount,
  searchValue,
  onClientSearchChange,
}: ClientListTableProps) => {
  const navigate = useNavigate();

  const handleUploadFile = (client: ClientInformation) => {
    navigate(`${ROUTES.ADMIN.FILE_UPLOAD}?clientId=${client.id}`);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCellClick = (params: any) => {
    // Don't navigate if clicking on the actions column
    if (params.field !== "actions") {
      onViewClient(params.row);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Client Name",
      flex: 2,
      minWidth: 200,
    },
    {
      field: "type",
      headerName: "Client Type",
      flex: 1,
      minWidth: 150,
      valueGetter: (params, row) => row.type?.description || "N/A",
    },
    {
      field: "cadenceText",
      headerName: "Cadence",
      flex: 1,
      minWidth: 120,
      valueGetter: (params, row) => row.frequency?.cadence?.text || "N/A",
    },
    {
      field: "lastRecievedDate",
      headerName: "Last Received",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => {
        if (!params) return "N/A";
        return new Date(params).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
      valueGetter: (params, row) => row.frequency?.lastRecievedDate,
    },
    {
      field: "nextExpectedDate",
      headerName: "Next Expected",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => {
        if (!params) return "N/A";
        return new Date(params).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
      valueGetter: (params, row) => row.frequency?.nextExpectedDate,
    },
    {
      field: "fileCount",
      headerName: "Files",
      flex: 0.6,
      minWidth: 80,
      align: "center",
      headerAlign: "center",
      valueGetter: (params, row) => getFileCount(row.id),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      flex: 1,
      minWidth: 150,
      headerAlign: "right",
      align: "right",
      renderCell: (params: GridRenderCellParams<ClientWithFrequency>) => (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Tooltip title="View Files">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                onViewClient(params.row);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Upload File">
            <IconButton
              size="small"
              color="success"
              onClick={(e) => {
                e.stopPropagation();
                handleUploadFile(params.row);
              }}
            >
              <UploadFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: "action.hover",
          borderRadius: 2,
          p: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <GroupIcon sx={{ color: "primary.main", fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Clients List
          </Typography>
        </Box>
        <AutoComplete
          label="Client Search"
          suggestions={allClients}
          selectedValue={searchValue}
          onChange={onClientSearchChange}
          sx={{ width: 250 }}
        />
      </Box>
      <CustomizedDataGrid
        data={clients}
        columns={columns}
        isLoading={false}
        initialSortingField="name"
        rowSelection
        type="client"
        onCellClick={handleCellClick}
        height={635}
      />
    </Box>
  );
};

export default ClientListTable;
